import _ from "lodash";
import React from "react";
import classes from "./UIInputNumberField.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { AddOutlined, MinusOutlined } from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";

const isNumber = evt => {
  const charCode = evt.which;

  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    return false;
  }
  return true;
};

const getRegex = ({ regexString }) => new RegExp(regexString);

class UIInputNumberField extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
    };

    const { value, disabled, shouldUpdateValueOnMount } = props;
    if (_.isNull(value) && !disabled && shouldUpdateValueOnMount) {
      this.updateValue(0);
    }
  }

  componentDidUpdate() {
    const { value, minDuration, maxDuration, showErrorState } = this.props;
    if (showErrorState) {
      this.setState({
        error: value < minDuration || value > maxDuration,
      });
    }
  }

  isValidValue = value => {
    return value != 0 && _.trim(_.toString(value));
  };

  handleKeyPress = e => {
    if (e.key === "Enter") {
      const param = {};
      param[e.target.name] = e.target.value;
      if (this.props.onEnter) {
        this.props.onEnter(param);
      }
    }

    return isNumber(e);
  };

  shouldShowViewEmpty = () => {
    return false;
  };

  onBlurLocal = e => {
    const { isStringInput, regexString, isFloatValue } = this.props;
    let value = e.target.value;
    const regex = getRegex({ regexString });
    if ((!_.trim(value) || !regex.test(value)) && !isStringInput) {
      value = 0;
    }
    const updatedValue = isStringInput ? value : parseFloat(value);
    const shouldSkipUpdate =
      isFloatValue &&
      this.getShouldSkipUpdateValue({
        value: updatedValue,
      });
    if (shouldSkipUpdate) return;
    this.updateValue(updatedValue);
    this.onBlur({ value: updatedValue });
  };

  normalizeNumericInput = num => {
    const [integerPart, decimalPart] = _.split(_.toString(num), ".");
    const trimmedIntegerPart = integerPart.replace(/^0+(?=[1-9])/, ""); // Remove leading zeros

    if (_.includes(num, ".")) {
      // If there is a decimal point, return integer part and decimal part
      return !_.isNil(decimalPart) && !_.isEmpty(decimalPart)
        ? `${parseFloat(trimmedIntegerPart)}.${decimalPart}`
        : `${parseFloat(trimmedIntegerPart)}.`;
    } else {
      // No decimal part, return integer part
      return parseFloat(trimmedIntegerPart);
    }
  };

  handleChange = e => {
    const {
      isStringInput,
      regexString,
      isFloatValue,
      normalizeNumericInput,
    } = this.props;
    const regex = getRegex({ regexString });

    let value = e.target.value;

    if (normalizeNumericInput) {
      value = this.normalizeNumericInput(value);
    }

    const shouldSkipUpdate =
      isFloatValue && this.getShouldSkipUpdateValue({ value });
    if (shouldSkipUpdate) return;
    if (regex.test(value) || isStringInput || !value) {
      this.updateValue(value);
    }
  };

  increaseValue = () => {
    const { value, stepperValue, isFloatValue, disabled } = this.props;
    if (disabled) return;

    const updatedValue = parseFloat(value ? value : 0) + stepperValue;
    const shouldSkipUpdate =
      isFloatValue &&
      this.getShouldSkipUpdateValue({
        value: updatedValue,
      });

    if (shouldSkipUpdate) return;
    this.updateValue(updatedValue);
  };

  decreaseValue = () => {
    const { value, stepperValue, disabled } = this.props;
    if (disabled) return;

    const updatedValue = parseFloat(value ? value : 0) - stepperValue;
    this.updateValue(updatedValue >= 0 ? updatedValue : 0);
  };

  getShouldSkipUpdateValue = ({ value }) => {
    // this is to skip the update value if the value length exceeds the given limit
    // this can be handled using max length but we also need support to add decimal value
    // for example if the max length is 3 and the value is 999.5 then it should be allowed
    // but >=1000 should not be allowed
    const updatedValue = typeof value === "string" ? value : _.toString(value);
    const { maxNumLength } = this.props;
    const valueLength = updatedValue.length;
    if (valueLength > maxNumLength && !_.includes(updatedValue, ".")) {
      return true;
    }
    return false;
  };

  renderEdit = () => {
    const {
      inputStyle,
      disabled,
      value,
      name,
      numberLabel,
      errorStyle,
      showControlButtons,
      fallbackValue,
      inputType,
      maxLength,
      customInputTextClass = {},
      buttonSize,
      shouldFocusOnMount = false,
      dsVersion,
      numFieldContainerStyle,
      testId,
      placeholder,
      stepperValue,
    } = this.props;
    const { error } = this.state;
    let updatedInputStyle = { ...inputStyle };
    if (disabled) {
      updatedInputStyle = { ...inputStyle, cursor: "not-allowed" };
    }
    if (error) {
      updatedInputStyle = { ...updatedInputStyle, ...errorStyle };
    }
    const isDsV2 = dsVersion === "2.0";

    const inputText = classNames(
      { [classes.inputText]: true },
      { [classes.disabledInputBox]: disabled },
      { [classes.error]: !!error },
      { [customInputTextClass]: true },
      { [classes.inputTextV2]: isDsV2 },
      { "text-body": isDsV2 }
    );

    const numFieldClasses = classNames(classes.numFieldContainer, {
      [classes.numFieldContainerV2]: isDsV2,
    });

    const iconButtonCommonProps = {
      variant: isDsV2 ? "neutral" : "outlined-subtle",
      type: isDsV2 ? "outlined" : "",
      dsVersion,
    };

    return (
      <div className={classes.container}>
        {numberLabel && <div className={classes.labelText}>{numberLabel}</div>}
        <div className={numFieldClasses} style={numFieldContainerStyle}>
          {showControlButtons && (
            <IconButton
              icon={<MinusOutlined />}
              variant="outlined-subtle"
              onClick={this.decreaseValue}
              size={buttonSize}
              className={classes.button}
              testId={`${testId}-decrement`}
              {...iconButtonCommonProps}
            />
          )}
          <input
            onKeyPress={e => this.handleKeyPress(e)}
            disabled={disabled}
            style={updatedInputStyle}
            className={inputText}
            type={inputType}
            value={value || value === 0 ? value : fallbackValue}
            onChange={e => this.handleChange(e)}
            onBlur={this.onBlurLocal}
            name={name}
            min={0}
            maxLength={maxLength}
            autoFocus={shouldFocusOnMount}
            data-test-id={`${testId}-input`}
          />
          {showControlButtons && (
            <IconButton
              icon={<AddOutlined />}
              variant="outlined-subtle"
              onClick={this.increaseValue}
              size={buttonSize}
              className={classes.button}
              testId={`${testId}-increment`}
              {...iconButtonCommonProps}
            />
          )}
        </div>
      </div>
    );
  };
}
UIInputNumberField.defaultProps = {
  ...UIBaseComponent.defaultProps,
  inputStyle: {},
  value: 0,
  numberLabel: "",
  showControlButtons: true,
  fallbackValue: 0,
  inputType: "text",
  maxLength: "3",
  isStringInput: false,
  showErrorState: false,
  maxDuration: 999,
  minDuration: 0,
  errorStyle: {},
  regexString: "^[0-9\b]+$",
  stepperValue: 1,
  isFloatValue: false,
  maxNumLength: 3,
  buttonSize: "small",
  shouldUpdateValueOnMount: true,
  dsVersion: "1.0",
  numFieldContainerStyle: {},
  normalizeNumericInput: false,
  placeholder: "",
};

UIInputNumberField.propTypes = {
  ...UIBaseComponent.propTypes,
  onBlur: PropTypes.func,
  inputStyle: PropTypes.object,
  onEnter: PropTypes.func,
  numberLabel: PropTypes.string,
  maxLength: PropTypes.string,
  isStringInput: PropTypes.bool,
  showErrorState: PropTypes.bool,
  maxDuration: PropTypes.number,
  minDuration: PropTypes.number,
  buttonSize: PropTypes.string,
  errorStyle: PropTypes.object,
  regexString: PropTypes.string,
  stepperValue: PropTypes.number,
  isFloatValue: PropTypes.bool,
  maxNumLength: PropTypes.number,
  shouldUpdateValueOnMount: PropTypes.bool,
  dsVersion: PropTypes.string,
  numFieldContainerStyle: PropTypes.object,
  normalizeNumericInput: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default UIInputNumberField;
